import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectors } from '@opallabs/wormhole'
import { AUTO_COLOR_BY, makeSortOptions } from './sortHelpers'
import { WeekViewSortType } from './types'
import { useWeekViewSettings } from './WeekViewSettingsContext'

/**
 * What the week can be sorted and coloured by, and whether the label sets both
 * lists are built from have arrived.
 *
 * The control bar above the week and the week itself both need these, and the
 * two sit in different React trees, so neither can hand them to the other.
 */
export const useWeekViewSortOptions = () => {
  const { colorBy, selectedSort } = useWeekViewSettings()
  const labelSets = useSelector(selectors.selectV1LabelSets)
  const currentWeekLabelSets = useSelector(selectors.selectCurrentWeekLabelSets)

  const sortOptions = useMemo(() => {
    const weekLabelSets =
      currentWeekLabelSets === null ? null : Object.values(currentWeekLabelSets)
    const options = makeSortOptions(weekLabelSets)

    // Preserve selected LabelSet sort even if not used in current week
    // This allows sort preference to persist across week navigation
    if (selectedSort?.type === WeekViewSortType.LabelSet) {
      const sortExists = options.some(
        (opt) =>
          opt.type === selectedSort.type &&
          String(opt.id) === String(selectedSort.id)
      )

      if (!sortExists) {
        return [...options, selectedSort]
      }
    }

    return options
  }, [currentWeekLabelSets, selectedSort])

  // Color-By options are the subset of sorts that map to a card color today:
  // Channel and any label set, plus the "Sort" (match-sort) default. (Account is
  // excluded — an account inherits its channel's color, so it's not a
  // distinct card color; Time/None have no color.)
  const colorByOptions = useMemo(() => {
    const options = [
      AUTO_COLOR_BY,
      ...sortOptions.filter(
        (opt) =>
          opt.type === WeekViewSortType.Channel ||
          opt.type === WeekViewSortType.LabelSet
      ),
    ]

    // Preserve a selected LabelSet color choice across week navigation even
    // when no moment in the current week carries that set (mirrors the
    // selectedSort handling above).
    if (colorBy?.type === WeekViewSortType.LabelSet) {
      const exists = options.some(
        (opt) =>
          opt.type === colorBy.type && String(opt.id) === String(colorBy.id)
      )

      if (!exists) return [...options, colorBy]
    }

    return options
  }, [sortOptions, colorBy])

  return {
    colorByOptions,
    isLoadingLabelSets: labelSets === null,
    sortOptions,
  }
}
